// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { type ConsentState, subscribeToConsentState } from '@extern/consent/state';
import {
  addIntegration,
  browserTracingIntegration,
  captureRouterTransitionStart,
  dedupeIntegration,
  init,
  replayIntegration,
} from '@sentry/nextjs';

import { isExpiredSessionError } from '@/infra/auth/expired-session';

init({
  dsn: 'https://7a2cdb0239adeb814cabd36148149756@o4508445415571456.ingest.us.sentry.io/4508445420552192',

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Session Replay is added later, only for visitors who accepted statistics — see the bottom of
  // this file. Error monitoring and tracing stay on for everyone: they are what keeps the product
  // operable, not analytics about the visitor.
  integrations: [dedupeIntegration(), browserTracingIntegration()],

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1.0,

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  enabled: process.env.NODE_ENV === 'production',

  // Third-party Segment/Appcues device-mode scripts (EXTERN-FRONT-END-17Y, MW): destination
  // load failures race with analytics.page() inside vendor bundles — not app bugs. The
  // pattern covers the whole `next-integrations/` tree: Segment serves classic destinations
  // under `integrations/` and action destinations (GA4 Web, which loads gtag from
  // googletagmanager.com and is blocked by Safari ITP) under `actions/`. Our own chunks
  // ship under `_next/static/`, so nothing of ours matches.
  //
  // Browser-extension bundles injected into the page (EXTERN-FRONT-END-1KB): the app
  // serves no /executors/ path, and those rejections fire on unrelated routes for a
  // single Edge profile without touching the UI. Match on the script path, never on
  // the message — "Cannot read properties of undefined" is a real app bug elsewhere.
  denyUrls: [/next-integrations\//, /appcues/, /\/executors\//],

  /**
   * A 401 is not a defect the app can fix by shipping code: the session ran out while the tab was
   * idle, and `useFetchAuth` already answers it — refresh the token, replay once, and fall back to
   * the sign-in redirect. Matching on the status rather than the message keeps a real
   * "Unauthorized" thrown from anywhere else reportable.
   */
  beforeSend: (event, hint) => (isExpiredSessionError(hint.originalException) ? null : event),

  ignoreErrors: [
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    // Clerk session touch failures from flaky network / suspended tabs (EXTERN-FRONT-END-14Z, NZ).
    // Do not broaden to generic "Failed to fetch" — that can hide real app bugs.
    /ClerkJS: Network error/,
    // Transport failures, scoped to the host the SDK appends to the message
    // (EXTERN-FRONT-END-R3, 1D3). denyUrls cannot reach these: browser extensions patch
    // window.fetch, so the stack interleaves extension and app frames and often ends in one
    // of ours, and denyUrls reads only the last frame.
    //
    // The LinkedIn Insight Tag conversion pixel. Ad blockers drop it; nothing of ours reads it.
    /Failed to fetch \(px\.ads\.linkedin\.com\)/,
    // The ~55s Clerk session token refresh. A rejected fetch is a transport failure, never an
    // auth rejection — a 401 arrives as a response. A DNS, proxy or CORS outage on that host
    // would also land here, so watch Clerk's status page rather than this issue.
    /Failed to fetch \(clerk\.extern\.com\)/,
  ],
});

/**
 * Sentry initializes outside React, before any component mounts, so it reads the consent store
 * directly rather than through `useConsent`. Replay records the DOM of an authenticated product —
 * one of the three session recorders Matt flagged — so it stays off until statistics consent is
 * granted, and is added in place at that point rather than waiting for a reload: unlike Segment
 * and PostHog, Sentry supports adding an integration to a running client.
 *
 * Note `sendDefaultPii: true` above is unchanged and still applies to error events for everyone.
 * That is a separate call from this gate and needs its own decision.
 */
let unsubscribeFromConsent: (() => void) | undefined;
let isReplayEnabled = false;

function enableReplayOnConsent(state: ConsentState): void {
  if (isReplayEnabled || !state.categories.statistics) {
    return;
  }

  isReplayEnabled = true;
  addIntegration(
    replayIntegration({
      // Capture the upload response status so a client-side abort stops looking identical
      // to a 502 from storage. Bodies stay off: this request carries the student's resume.
      networkDetailAllowUrls: ['/api/portfolio/upload'],
      networkCaptureBodies: false,
    }),
  );
  unsubscribeFromConsent?.();
}

// The subscription fires synchronously with the current state, which is why the unsubscribe is a
// `let` the callback reads rather than a const it closes over before assignment.
unsubscribeFromConsent = subscribeToConsentState(enableReplayOnConsent);

export const onRouterTransitionStart = captureRouterTransitionStart;
